import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Switch, Typography, useSnackbar } from '@web-chat-core/ui-kit';
import { observer } from 'mobx-react-lite';
import { Modal } from 'features/Modals';
import { CallJoinPolicy } from 'features/AccessRequests/api/accessRequestsApi';
import { accessRequestsSettingsStore } from 'state/store';
import {
    usePersistNotificationsSetting,
    usePersistSoundSetting,
} from 'features/AccessRequests/hooks/usePersistAccessRequestsSettings';
import { isCallInitiator } from 'features/AccessRequests/utils/canBypassWaitingRoom';
import { canAuthorizedUserConfigureAccessRequestAlerts } from 'features/CallSettings/hooks/useCanSeeCallSettings';
import { useCallPolicyConfig } from 'features/CallSettings/hooks/useCallPolicyConfig';
import { useModal } from 'useCase/modal/useModal';
import { VCS_JOIN_POLICY } from 'consts';
import { isLocalUserModerator } from 'useCase/moderation/status/moderators';
import { selectIsAuthorized, selectLocalParticipant } from 'state/selectors';
import { useTranslation } from 'react-i18next';
import {
    Devider,
    JoinPolicyRadio,
    JoinPolicyRadioContainer,
    JoinPolicyRadioWrapper,
    SectionTitle,
    SettingsContent,
    ToggleContainer,
    ToggleDescription,
    ToggleInfo,
    ToggleLabel,
    ToggleRow,
} from './view';

const JOIN_POLICY_OPTIONS = [
    { value: VCS_JOIN_POLICY.ALL, labelKey: 'vks_request_access_all' },
    { value: VCS_JOIN_POLICY.GUESTS_WITH_APPROVAL, labelKey: 'vks_request_access_guests_with_permission' },
    { value: VCS_JOIN_POLICY.ALL_WITH_APPROVAL, labelKey: 'vks_request_access_all_with_permission' },
] as const;

export const CallSettingsModal: React.FC = observer(() => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const {
        joinPolicy: configJoinPolicy,
        moderatorsApprovalOnly: configModeratorsApprovalOnly,
        applyChanges,
    } = useCallPolicyConfig();

    const [joinPolicy, setJoinPolicy] = useState<CallJoinPolicy>(configJoinPolicy);
    const [moderatorsApprovalOnly, setModeratorsApprovalOnly] = useState<boolean>(configModeratorsApprovalOnly);

    useEffect(() => {
        setJoinPolicy(configJoinPolicy);
        setModeratorsApprovalOnly(configModeratorsApprovalOnly);
    }, [configJoinPolicy, configModeratorsApprovalOnly]);

    const { soundEnabled, notificationsEnabled } = accessRequestsSettingsStore;
    const persistSoundSetting = usePersistSoundSetting();
    const persistNotificationsSetting = usePersistNotificationsSetting();
    const isAuthorized = selectIsAuthorized();
    const isModerator = isLocalUserModerator();
    const isInitiator = isCallInitiator();
    const isOnPrejoin = !selectLocalParticipant();
    const { closeModal } = useModal();

    // Prejoin: создатель звонка (из URL/embedded/Livekit JWT); в комнате — модератор по metadata.
    const isCanManageJoinPolicy = isOnPrejoin ? isInitiator : isModerator;
    const isCanConfigureAccessRequests = isModerator || isInitiator;

    // Инициатор/модератор видят секцию всегда; авторизованный только если moderatorsApprovalOnly выключен
    const isShowSoundAndNotifications = isCanConfigureAccessRequests || canAuthorizedUserConfigureAccessRequestAlerts();
    const isApprovalPolicyRequired = joinPolicy !== VCS_JOIN_POLICY.ALL;
    const isSoundAndNotificationsDisabled = !isApprovalPolicyRequired;
    const isModeratorsApprovalDisabled = !isCanManageJoinPolicy || !isApprovalPolicyRequired;

    useEffect(() => {
        if (isAuthorized && !isCanConfigureAccessRequests && !canAuthorizedUserConfigureAccessRequestAlerts()) {
            closeModal();
        }
    }, [configJoinPolicy, configModeratorsApprovalOnly, isAuthorized, isCanConfigureAccessRequests, closeModal]);

    const joinPolicyOptions = useMemo(
        () =>
            JOIN_POLICY_OPTIONS.map((option) => ({
                ...option,
                label: t(option.labelKey),
            })),
        [t],
    );

    const handleJoinPolicyChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const policy = e.target.value as CallJoinPolicy;
            const nextModeratorsApprovalOnly = policy === VCS_JOIN_POLICY.ALL ? false : moderatorsApprovalOnly;
            setJoinPolicy(policy);
            if (policy === VCS_JOIN_POLICY.ALL) {
                setModeratorsApprovalOnly(false);
            }
            applyChanges({ joinPolicy: policy, moderatorsApprovalOnly: nextModeratorsApprovalOnly });
        },
        [applyChanges, moderatorsApprovalOnly],
    );

    const handleModeratorsChange = (value: boolean) => {
        setModeratorsApprovalOnly(value);
        applyChanges({ joinPolicy, moderatorsApprovalOnly: value });
    };

    const onPersistError = () => {
        showSnackbar(t('web_videoconference_error'), {
            type: 'error',
            testId: 'snackbar-call-settings-save-sound-notifications-error',
        });
    };

    const handleSoundChange = (value: boolean) => {
        accessRequestsSettingsStore.setSound(value);
        void persistSoundSetting(value).then(({ hasError }) => {
            if (hasError) onPersistError();
        });
    };

    const handleNotificationsChange = (value: boolean) => {
        accessRequestsSettingsStore.setNotifications(value);
        void persistNotificationsSetting(value).then(({ hasError }) => {
            if (hasError) onPersistError();
        });
    };

    return (
        <Modal header={<Typography variant="body1">{t('vks_request_call_settings')}</Typography>}>
            <SettingsContent>
                {isCanManageJoinPolicy && (
                    <>
                        <SectionTitle variant="body3">{t('vks_request_connection_access_available')}</SectionTitle>
                        <JoinPolicyRadioContainer>
                            {joinPolicyOptions.map((option) => (
                                <JoinPolicyRadioWrapper key={option.value}>
                                    <JoinPolicyRadio
                                        checked={joinPolicy === option.value}
                                        id={`call-join-policy-${option.value}`}
                                        value={option.value}
                                        label={option.label}
                                        onChange={handleJoinPolicyChange}
                                        name="callJoinPolicy"
                                        oneRow
                                        cy={`callSettingsJoinPolicy${option.value}`}
                                    />
                                </JoinPolicyRadioWrapper>
                            ))}
                        </JoinPolicyRadioContainer>
                        <Devider />
                        <SectionTitle variant="body3">{t('vks_request_connection_approval_by')}</SectionTitle>
                        <ToggleRow>
                            <ToggleInfo>
                                <ToggleLabel variant="body5">{t('vks_request_approval_only_moderators')}</ToggleLabel>
                                <ToggleDescription>
                                    {t('vks_request_disabled_all_authorized_except_guests')}
                                </ToggleDescription>
                            </ToggleInfo>
                            <Switch
                                checked={moderatorsApprovalOnly}
                                disabled={isModeratorsApprovalDisabled}
                                onChange={(e) => handleModeratorsChange(e.target.checked)}
                            />
                        </ToggleRow>
                    </>
                )}

                {isShowSoundAndNotifications && (
                    <>
                        {isCanManageJoinPolicy && <Devider />}
                        <SectionTitle variant="body3">{t('vks_request_sound_and_notifications')}</SectionTitle>
                        <ToggleContainer>
                            <ToggleRow>
                                <ToggleInfo>
                                    <ToggleLabel variant="body5">{t('vks_request_sound')}</ToggleLabel>
                                    <ToggleDescription>{t('vks_request_sound_waiting_users')}</ToggleDescription>
                                </ToggleInfo>
                                <Switch
                                    checked={soundEnabled}
                                    disabled={isSoundAndNotificationsDisabled}
                                    onChange={(e) => handleSoundChange(e.target.checked)}
                                />
                            </ToggleRow>

                            <ToggleRow>
                                <ToggleInfo>
                                    <ToggleLabel variant="body5">{t('vks_request_notifications')}</ToggleLabel>
                                    <ToggleDescription>{t('vks_request_notification_waiting_users')}</ToggleDescription>
                                </ToggleInfo>
                                <Switch
                                    checked={notificationsEnabled}
                                    disabled={isSoundAndNotificationsDisabled}
                                    onChange={(e) => handleNotificationsChange(e.target.checked)}
                                />
                            </ToggleRow>
                        </ToggleContainer>
                    </>
                )}
            </SettingsContent>
        </Modal>
    );
});
